import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

const initialState = {
  formTemplatesList: [],
  formTemplates: [],
  currentFormTemplate: null,
  isLoading: false,
  pendingApprovalFormsList: [],
  totalFormTemplatesCount: 0,
};

export const createFormTemplate = createAsyncThunk(
  "visit_templates/createFormTemplate",
  async ({ formTemplate, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates?account_id=${selectedAccount}`;

    try {
      const response = await axios.post(
        url,
        { visit_template: formTemplate },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const editFormTemplate = createAsyncThunk(
  "visit_templates/editFormTemplate",
  async ({ formTemplate, formTemplateId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/${formTemplateId}?account_id=${selectedAccount}`;

    try {
      const response = await axios.put(
        url,
        { visit_template: formTemplate },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateReportDetails = createAsyncThunk(
  "visit_templates/update_report_details",
  async ({ formTemplate, formTemplateId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/${formTemplateId}/update_report_details?account_id=${selectedAccount}`;

    try {
      const response = await axios.patch(
        url,
        { visit_template: formTemplate },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);


export const deleteFormTemplate = createAsyncThunk(
  "visit_templates/deleteFormTemplate",
  async ({ formTemplateId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/${formTemplateId}?account_id=${selectedAccount}`;

    try {
      await axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });

      toast.success("Form template deleted successfully");
      return { formTemplateId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getAllFormTemplates = createAsyncThunk(
  "visit_templates/getAllFormTemplates",
  async (
    {
      searchQuery,
      searchTags,
      page,
      rowsPerPage,
      selectedAccount,
      archive = false,
      sort,
      order,
      scope,
    },
    { rejectWithValue }
  ) => {
    const bearerToken = getToken();
    let url = `${apiBaseUrl}/visit_templates?q=${searchQuery}&tags=${searchTags}&page=${page + 1}&per_page=${rowsPerPage}&account_id=${selectedAccount}&archive=${archive}`;

    if (scope) {
      url += `&scope=${encodeURIComponent(scope)}`;
    }

    // Add sorting parameters if provided
    if (sort && order) {
      url += `&sort=${sort}&order=${order}`;
    }

    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const importVisitTemplate = createAsyncThunk(
  "visit_templates/importVisitTemplate",
  async ({ id, selectedAccount, groupIds, keepPrivate }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/${id}/import?account_id=${selectedAccount}`;

    try {
      const response = await axios.post(
        url,
        { group_ids: groupIds, keep_private: keepPrivate },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );

      toast.success("Form template imported successfully");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// The form and its tests only — no groups, no companies, no test functions.
// Used by the exam wizard's "Choose Tests" step, which needs to know which
// tests a form carries and nothing more. Deliberately a separate thunk from
// getFormTemplate so this lean payload never lands in `currentFormTemplate`,
// which the form preview and report designer read expecting the full shape.
export const getFormTemplateSummary = createAsyncThunk(
  "visit_templates/getFormTemplateSummary",
  async ({ formTemplateId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/${formTemplateId}?account_id=${selectedAccount}&view=summary`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// The report layouts of a form's tests, in order, as an array of JSON strings.
// Separate from getFormTemplate: the full form payload no longer carries them.
export const getFormTemplateReportData = createAsyncThunk(
  "visit_templates/getFormTemplateReportData",
  async ({ formTemplateId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/${formTemplateId}/report_data?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${bearerToken}` },
      });
      return response.data.reports || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to load report data");
    }
  }
);

export const getFormTemplate = createAsyncThunk(
  "visit_templates/getFormTemplate",
  async ({ formTemplateId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/${formTemplateId}?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const copyFormTemplate = createAsyncThunk(
  "visit_templates/copyFormTemplate",
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/${id}/copy_visit_template?account_id=${selectedAccount}`;

    try {
      const response = await axios.post(url, { id }, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getFormTemplates = createAsyncThunk(
  "visit_templates/getFormTemplates",
  async (selectedAccount,
    { rejectWithValue }
  ) => {

    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/unpaginated_users_templates?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Archive
export const archiveFormTemplate = createAsyncThunk(
  "visit_templates/archiveFormTemplate",
  async ({ formTemplateId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/${formTemplateId}/archive?account_id=${selectedAccount}`;

    try {
      await axios.post(url, {}, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Form template archived successfully");
      return { formTemplateId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to archive" });
    }
  }
);

// Unarchive
export const unarchiveFormTemplate = createAsyncThunk(
  "visit_templates/unarchiveFormTemplate",
  async ({ formTemplateId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/${formTemplateId}/unarchive?account_id=${selectedAccount}`;

    try {
      await axios.post(url, {}, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Form template unarchived successfully");
      return { formTemplateId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to unarchive" });
    }
  }
);
export const updateAssociatedTestsOrder = createAsyncThunk(
  "visit_templates/updateAssociatedTestsOrder",
  async ({ formTemplateId, order, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/${formTemplateId}/update_order?account_id=${selectedAccount}`;

    try {
      const response = await axios.patch(
        url,
        { visit_template: { order_test_ids: order } },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchPendingForms = createAsyncThunk(
  "visit_templates/fetchPendingForms",
  async ({ searchQuery, page, rowsPerPage, selectedAccount, onlyApproved }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/pending_approval_forms?page=${page}&per_page=${rowsPerPage}&account_id=${selectedAccount}&only_approved=${onlyApproved}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const formTemplateSlice = createSlice({
  name: "formTemplate",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllFormTemplates.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllFormTemplates.fulfilled, (state, action) => {
        state.formTemplatesList = action.payload.visit_templates;
        state.isLoading = false;
        state.totalFormTemplatesCount = action.payload.meta.total_count;
      })
      .addCase(getAllFormTemplates.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getFormTemplates.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFormTemplates.fulfilled, (state, action) => {
        state.formTemplates = action.payload.visit_templates;
        state.isLoading = false;
      })
      .addCase(getFormTemplates.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getFormTemplate.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFormTemplate.fulfilled, (state, action) => {
        state.currentFormTemplate = action.payload;
        state.isLoading = false;
      })
      .addCase(getFormTemplate.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(updateReportDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateReportDetails.fulfilled, (state, action) => {
        state.currentFormTemplate = action.payload;
        state.isLoading = false;
      })
      .addCase(updateReportDetails.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(editFormTemplate.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(editFormTemplate.fulfilled, (state, action) => {
        state.currentFormTemplate = action.payload;
        state.isLoading = false;
      })
      .addCase(editFormTemplate.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchPendingForms.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPendingForms.fulfilled, (state, action) => {
        state.pendingApprovalFormsList = action.payload.visit_templates;
        state.totalFormTemplatesCount = action.payload.meta.total_count
        state.isLoading = false;
      })
      .addCase(fetchPendingForms.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteFormTemplate.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteFormTemplate.fulfilled, (state, action) => {
        state.formTemplatesList = state.formTemplatesList.filter(
          (template) => template.id !== action.payload.formTemplateId
        );
        state.isLoading = false;
      })
      .addCase(deleteFormTemplate.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(archiveFormTemplate.fulfilled, (state, action) => {
        state.formTemplatesList = state.formTemplatesList.filter(
          (template) => template.id !== action.payload.formTemplateId
        );
        state.isLoading = false;
      })
      .addCase(unarchiveFormTemplate.fulfilled, (state, action) => {
        state.formTemplatesList = state.formTemplatesList.filter(
          (template) => template.id !== action.payload.formTemplateId
        );
        state.isLoading = false;
      })
      .addCase(updateAssociatedTestsOrder.fulfilled, (state, action) => {
        state.currentFormTemplate = action.payload;
        toast.success("Order updated successfully");
      })
      .addCase(updateAssociatedTestsOrder.rejected, (state, action) => {
        toast.error(`Failed to update order: ${action.payload}`);
      })
      .addCase(copyFormTemplate.fulfilled, (state, action) => {
        toast.success("Form Template copied successfully");
      })
      .addCase(copyFormTemplate.rejected, (state, action) => {
        toast.error(`Failed to copy: ${action.payload}`);
      });

      builder
        .addCase(importVisitTemplate.pending, (state) => {
          state.isLoading = true;
        })
        .addCase(importVisitTemplate.fulfilled, (state) => {
          state.isLoading = false;
        })
        .addCase(importVisitTemplate.rejected, (state) => {
          state.isLoading = false;
        });
  },
});

export default formTemplateSlice.reducer;
